/**
 * Shipping service — centralises shipping cost and address validation API calls.
 *
 * Covers: smart shipping cost calculation, address parsing/validation,
 * and sales tax computation.
 */
import api from './api'

// ---------------------------------------------------------------------------
// Shipping Cost
// ---------------------------------------------------------------------------

export function getSmartShippingCost(data) {
  return api
    .post('/shipping/getSmartShippingCost', data)
    .then((response) => {
      logShippingRateProvider(response, 'shippingService')
      return response
    })
}

// Response headers stamped by api/src/endpoints/shipping.py. CORS exposes
// them (api/src/plugins/__init__.py); axios lower-cases the names.
const PROVIDER_HEADER = 'x-shipping-rate-provider'
const FALLBACK_HEADER = 'x-shipping-rate-fallback'

function readHeader(headers, name) {
  if (!headers) return undefined
  const value =
    typeof headers.get === 'function' ? headers.get(name) : headers[name]
  return typeof value === 'string' && value ? value : undefined
}

/**
 * Log which provider rated a /shipping/getSmartShippingCost quote, so an
 * engineer can see in the browser console what a shopper cannot: whether the
 * back office rated it or ShipEngine silently stood in.
 *
 *   deconetwork           -> console.info
 *   shipengine + fallback -> console.warn (back office tried, did not rate)
 *   shipengine (pinned)   -> console.info
 *   none                  -> console.error (no provider could rate)
 *   none + fallback-disabled -> console.error saying the SA switch is OFF
 *   headers missing       -> quiet console.debug
 *
 * NEVER throws: a diagnostics aid must not break a shopper's cart.
 *
 * @param {object} response  the axios response (data + headers)
 * @param {string} [context] which caller (for the debug line only)
 */
export function logShippingRateProvider(response, context = 'unknown') {
  try {
    const headers = response && response.headers
    const provider = readHeader(headers, PROVIDER_HEADER)
    const fallback = readHeader(headers, FALLBACK_HEADER)
    const amount = response ? response.data : undefined

    if (provider === 'deconetwork') {
      console.info(`[shipping] rate from back office: ${amount}`)
    } else if (provider === 'shipengine' && fallback) {
      console.warn(
        `[shipping] back office did NOT rate this (${fallback}); used ShipEngine`
      )
    } else if (provider === 'shipengine') {
      console.info(`[shipping] rate from ShipEngine (pinned): ${amount}`)
    } else if (
      provider === 'none' &&
      fallback &&
      /(^|;\s*)fallback-disabled$/.test(fallback)
    ) {
      // The SA switch "Use ShipEngine for shipping rates" is OFF, so
      // ShipEngine was never asked. Say so: this is a setting, not an outage.
      console.error(
        `[shipping] no rate: back office could not rate this (${fallback.replace(
          /;\s*fallback-disabled$/,
          ''
        )}) and the ShipEngine fallback is DISABLED in SA platform settings`
      )
    } else if (provider === 'none') {
      console.error(
        `[shipping] NO provider could rate this${fallback ? ` (${fallback})` : ''}`
      )
    } else {
      console.debug(`[shipping] no rate-provider headers (${context})`)
    }
  } catch (err) {
    // Diagnostics only; swallow everything.
  }
}

// ---------------------------------------------------------------------------
// Live-rate stores and the shipping origin (card: Live-rate shipping)
// ---------------------------------------------------------------------------

/**
 * The store shipping settings that price shipping WITHOUT a live rate.
 * Mirrors api/src/services/ship_from.py FLAT_SHIPPING_TYPES: every other
 * shipping-enabled store -- `smart_shipping` or no setting at all -- rates
 * live (Jake, 2026-10-21).
 */
export const FLAT_SHIPPING_TYPES = Object.freeze([
  'flat_rate',
  'flat_rate_range',
  'cart_percentage',
])

/** True when `store` charges shipping from a LIVE rate quote. */
export function storeRatesLive(store) {
  if (!store || !store.shipping_cost_enabled) return false
  return !FLAT_SHIPPING_TYPES.includes(store.shipping_cost_type)
}

/**
 * True when a getSmartShippingCost answer says the STORE has no shipping
 * origin (`X-Shipping-Rate-Fallback: unavailable; no-ship-origin`).
 * Retrying cannot help, so the caller shows a persistent contact message
 * rather than a "try again" toast.
 */
export function isNoShipOriginResponse(response) {
  const fallback = readHeader(response && response.headers, FALLBACK_HEADER)
  return typeof fallback === 'string' && /(^|;\s*)no-ship-origin$/.test(fallback)
}

// The id of the quote the server saved for this rate (Jake, 2026-10-22).
const QUOTE_ID_HEADER = 'x-shipping-quote-id'

/**
 * The server quote id a getSmartShippingCost answer carries, or null.
 *
 * Checkout on a live-rate store names this id (`shipping_quote_id`) and is
 * charged the amount the server STORED for it, never a client-posted one.
 * Null when the answer was not a rate, or the caller had no session.
 */
export function readShippingQuoteId(response) {
  const value = readHeader(response && response.headers, QUOTE_ID_HEADER)
  return value || null
}

/**
 * The shopper-facing error for a store with no shipping origin. `email` is
 * the store read's `shipping_support_email` (reply-to, else the
 * distributor Owner); with none, the shopper is asked to contact the store.
 */
export function shippingOriginMissingMessage(email) {
  const contact =
    typeof email === 'string' && email.trim()
      ? `contact support at ${email.trim()}`
      : 'contact the store'
  return (
    "Shipping can't be calculated for this store because it has no " +
    `shipping origin address, so orders can't be placed right now. Please ${contact}.`
  )
}

// ---------------------------------------------------------------------------
// Sales Tax
// ---------------------------------------------------------------------------

export function getSalesTax(data) {
  return api.post('/sales_tax/getSalesTax', data)
}

// ---------------------------------------------------------------------------
// Address Validation
// ---------------------------------------------------------------------------

export function getParsedAddress(location) {
  return api.post('/address_validation/getParsedAddress', { location })
}

export function parseAndValidateAddress(location) {
  return api.post('/address_validation/parseAndValidateAddress', { location })
}

export function validateAddress(location) {
  return api.post('/address_validation/validateAddress', { location })
}
